import { useCallback } from "react";

import { useAddPersonToListMutation } from "@/pages/Private/redux/list/list.api";
import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";

import { buildComposeAddBody, ComposeReachOutSource } from "./composeReachOut.utils";

interface UseComposeReachOutArgs {
	/** The prospect whose detail is refetched after the add, so its list membership is up to date. */
	prospectId: number;
	/** Hands the freshly created `ListPerson` id to the host panel, which opens the Chat view on it. */
	onListPersonCreated?: (listPersonId: number) => void;
	/** Surfaces a failure through the caller's snackbar (the action row renders exactly one). */
	onError: () => void;
}

export interface ComposeReachOutApi {
	/** Adds the lead to the Compose list; resolves `true` once a `ListPerson` exists for them. */
	startComposeReachOut: (source: ComposeReachOutSource) => Promise<boolean>;
	isComposing: boolean;
}

/**
 * One-click reach-out for a lead that is on no list (PREC-3825). Adds the lead to the company's
 * hidden Compose list via the shared `POST v1/list/person` endpoint — the same one the Global Search
 * "Write message" flow uses — and hands the created `ListPerson` id back so the panel can open its
 * Chat view. Enrichment is deliberately off: this only needs the list membership, not credits.
 */
export const useComposeReachOut = ({
	prospectId,
	onListPersonCreated,
	onError,
}: UseComposeReachOutArgs): ComposeReachOutApi => {
	const [addPersonToComposeList, { isLoading: isComposing }] = useAddPersonToListMutation();
	// The add creates a new list membership the already-loaded prospect detail doesn't know about —
	// refetch it so the "Add to ▾" menu flips to "Added to list".
	const [refetchProspectDetail] = useLazyGetContactProspectDetailQuery();

	const startComposeReachOut = useCallback(
		async (source: ComposeReachOutSource): Promise<boolean> => {
			const body = buildComposeAddBody(source);

			if (!body) {
				onError();

				return false;
			}

			try {
				const result = await addPersonToComposeList({
					...body,
					enrichEmail: false,
					enrichPhone: false,
				}).unwrap();

				const listPersonId = result?.data?.[0]?.id;

				if (!listPersonId) {
					onError();

					return false;
				}

				onListPersonCreated?.(listPersonId);
				void refetchProspectDetail(prospectId);

				return true;
			} catch {
				onError();

				return false;
			}
		},
		[addPersonToComposeList, refetchProspectDetail, prospectId, onListPersonCreated, onError]
	);

	return { startComposeReachOut, isComposing };
};
